import Whitespace from '@algbra/ui/components/Whitespace/Whitespace'
import { GatsbyResolvedAsset } from '@algbra/ui/types/image'
import { GatsbyImage } from 'gatsby-plugin-image'
import {
  filter,
  find,
  findIndex,
  indexOf,
  map,
  mapIndexed,
  pluck,
} from 'rambdax'
import 'swiper/css'
import { Swiper, SwiperSlide } from 'swiper/react'
import { resolveAsAttachment, resolveAsImage } from '@algbra/ui/resolvers'
import styles from './Carousel.module.scss'
import { useState } from 'react'
import SwiperCore, { Pagination } from 'swiper'
import Icon from '@algbra/ui/components/Icon/Icon'
import { getIconByName } from '@algbra/ui/icons'
import ReactDOM from 'react-dom'
import 'swiper/css/pagination'

export type CarouselImage = {
  id: string
  alternativeText?: string
  image?: GatsbyResolvedAsset
  fullWidth?: GatsbyResolvedAsset
  width?: number
  height?: number
  video?: GatsbyResolvedAsset
}
export type CarouselImageProps = CarouselImage & {
  hasPopup?: boolean
}

export type ZoneCarouselProps = {
  __typename: 'StrapiGQL_ComponentZonesCarousel'
  images: CarouselImage[]
  carouselOnly?: boolean
  hasPopup?: boolean
}
export const carouselSliderBreakpoints = {
  0: {
    spaceBetween: 15,
  },
  768: {
    spaceBetween: 60,
  },
}

export function CarouselImage(props: CarouselImageProps) {
  const { alternativeText, image, fullWidth, video, hasPopup = false } = props
  return (
    <div>
      {image && (
        <GatsbyImage
          image={resolveAsImage(image)}
          alt={alternativeText}
          imgClassName={styles.itemImage}
          loading="lazy"
        />
      )}
      {video && (
        <video
          src={resolveAsAttachment(video)}
          height={530}
          loop={true}
          autoPlay={true}
          playsInline={true}
          muted={true}
          className={styles.itemVideo}
        />
      )}
    </div>
  )
}

export default function ZoneCarousel(props: ZoneCarouselProps) {
  const [swiperRef, setSwiperRef] = useState<SwiperCore>()
  const [initialSlide, setInitialSlide] = useState<string>()
  const [isDialogVisible, toggleDialog] = useState(false)
  const viewImage = index => {
    setInitialSlide(index)
    toggleDialog(true)
  }
  const selectedImage = find(
    item => item.id === initialSlide,
    props.images || []
  )
  const imageListOnly = filter(image => !!image.fullWidth, props.images || [])
  const getSelectedImageIndex = id => {
    if (id) {
      return indexOf(id, pluck('id', imageListOnly || []))
    }
    return undefined
  }
  const carouselJSX = (
    <div className={styles.pull}>
      <Swiper
        slidesPerView={'auto'}
        freeMode={true}
        className={styles.carousel}
        breakpoints={carouselSliderBreakpoints}
        threshold={25}
      >
        {mapIndexed((carouselImage, index) => {
          return (
            <SwiperSlide
              className={styles.item}
              key={`image-${index}`}
              onClick={() => carouselImage.image && viewImage(carouselImage.id)}
            >
              <CarouselImage {...carouselImage} />
            </SwiperSlide>
          )
        }, props.images || [])}
      </Swiper>

      {isDialogVisible &&
        props.hasPopup &&
        ReactDOM.createPortal(
          <div className={styles.imageDialog}>
            <div className={styles.imageCloseBar}>
              <Icon
                icon={getIconByName('times')}
                className={styles.imageClose}
                onClick={() => toggleDialog(false)}
              />
            </div>
            <div className={styles.imageDialogContainer}>
              <Swiper
                slidesPerView={1}
                initialSlide={getSelectedImageIndex(selectedImage?.id)}
                modules={[Pagination]}
                loop={true}
                onSwiper={swiper => setSwiperRef(swiper)}
                pagination={{ clickable: true, dynamicBullets: true }}
              >
                {map(
                  ({ fullWidth, alternativeText, id }) => (
                    <SwiperSlide key={`fullWidth-${id}`}>
                      <GatsbyImage
                        image={resolveAsImage(fullWidth)}
                        alt={alternativeText}
                        imgClassName={styles.imageDialogImage}
                        objectFit="contain"
                      />
                    </SwiperSlide>
                  ),
                  imageListOnly || []
                )}
              </Swiper>

              <div
                className={styles.prev}
                onClick={() => swiperRef?.slidePrev()}
              >
                <Icon icon={getIconByName('arrow-back')} />
              </div>
              <div
                className={styles.next}
                onClick={() => swiperRef?.slideNext()}
              >
                <Icon icon={getIconByName('arrow-forward')} />
              </div>
            </div>
          </div>,
          document.getElementById('portal')
        )}
    </div>
  )
  if (props.carouselOnly) {
    return carouselJSX
  }
  return (
    <Whitespace type="md" title="List of images">
      {carouselJSX}
    </Whitespace>
  )
}
